<template>
  <header class="main-header">
    <div style="display: flex; flex-direction: row; align-items: center;">
      <h1 style="margin-left: 50px;">
        <span v-if="$route.path === '/taxonomy-editor'">Taxonomy Editor</span>
        <span v-else>Product Viewer</span>
      </h1>
      <span style="margin-left: 50px;">
        <v-btn v-if="$route.path === '/taxonomy-editor' && dataViewerRole !== 'none'" variant="text" to="/">Go To Product Viewer</v-btn>
        <v-btn v-if="$route.path !== '/taxonomy-editor' && taxonomyEditorRole !== 'none'" variant="text" to="/taxonomy-editor">Go To Taxonomy Editor</v-btn>
      </span>
    </div>

    <!-- Only show navigation when a user is logged in -->
    <nav v-if="user" class="nav">
      <!-- Hidden checkbox used to toggle the mobile menu -->
      <input type="checkbox" id="nav-toggle" class="nav-toggle" />
      <!-- Hamburger icon for mobile -->
      <label for="nav-toggle" class="nav-toggle-label">
        <span></span>
      </label>

      <!-- Navigation menu -->
      <ul class="nav-menu">
        <template v-if="$route.path !== '/taxonomy-editor'">
          <li>
            <router-link to="/">Catalog</router-link>
          </li>

          <li v-if="isAdmin">
            <router-link to="/admin">Admin</router-link>
          </li>

          <li v-if="isAdmin">
            <router-link to="/attribute-coverage">Attribute Coverage</router-link>
          </li>

          <!-- ====================================== -->
          <!-- DATABASE SELECTOR IN THE NAV BAR       -->
          <!-- ====================================== -->
          <li>
            <div class="db-selector-trigger">
              <label for="dbSelect">Environment:</label>
              <select
                id="dbSelect"
                v-model="selectedDb"
                @change="onDbChange"
              >
                <option value="production">Production</option>
                <option value="test">Test</option>
                <option value="staging">Staging</option>
              </select>
            </div>
          </li>
        </template>

        <!-- User menu -->
        <li class="user-menu">
          <div class="user-menu-trigger" @click="toggleUserMenu">
            {{ user.displayName || user.email || 'User' }}
            <span class="dropdown-arrow" :class="{ 'arrow-up': isUserMenuOpen }">▼</span>
          </div>
          <div class="user-dropdown" v-show="isUserMenuOpen">
            <div class="user-info">
              <div class="user-email">{{ user.email }}</div>
              <div v-if="isAdmin" class="user-role">Administrator</div>
            </div>
            <div class="menu-options">
              <button @click="logout" class="menu-option-btn">
                <span class="logout-btn">Sign Out</span>
              </button>
            </div>
          </div>
        </li>
      </ul>
    </nav>
  </header>
</template>

<script>
import { useRoute } from "vue-router";
import { getAuth, onAuthStateChanged, signOut, getIdTokenResult } from "firebase/auth";
import { useProductStore } from '@/store/product-viewer/productStore.js';
export default {
  name: "NavBar",
  data() {
    return {
      user: null,
      isAdmin: false,
      isUserMenuOpen: false,
      selectedDb: 'production',
      dataViewerRole: 'none',
      taxonomyEditorRole: 'none',
    };
  },
  methods: {
    async logout() {
      const auth = getAuth();
      try {
        await signOut(auth);
        this.isUserMenuOpen = false;
        this.$router.push('/login');
      } catch (error) {
        console.error("Error signing out:", error);
      }
    },
    
    toggleUserMenu() {
      this.isUserMenuOpen = !this.isUserMenuOpen;
    },
    
    closeUserMenuOnOutsideClick(event) {
      if (this.isUserMenuOpen && !event.target.closest('.user-menu')) {
        this.isUserMenuOpen = false;
      }
    },

    // Called when the user changes the DB from the dropdown
    onDbChange() {
      // Save the selection to localStorage
      localStorage.setItem('selectedDb', this.selectedDb);

      // Update the Pinia store
      const store = useProductStore();
      store.setSelectedDatabase(this.selectedDb);
    }
  },
  mounted() {
    const store = useProductStore();
    
    // 1. Check for a saved value in localStorage
    const savedDb = localStorage.getItem('selectedDb');
    
    // 2. If a value was found, use it. Otherwise, default to 'production'.
    this.selectedDb = savedDb || 'production';
    
    // 3. Sync this value with the Pinia store immediately
    store.setSelectedDatabase(this.selectedDb);

    // --- The rest of your existing mounted() code ---
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      this.user = user;
      if (user) {
        const tokenResult = await getIdTokenResult(user);
        this.isAdmin = tokenResult.claims["data-manager-role"] === "admin";
        this.dataViewerRole = tokenResult.claims["data-manager-role"]
        this.taxonomyEditorRole = tokenResult.claims["taxonomy-manager-role"]
      } else {
        this.isAdmin = false;
      }
    });

    document.addEventListener('click', this.closeUserMenuOnOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeUserMenuOnOutsideClick);
  },
};
</script>

<style scoped>
/* Main header styles */
.main-header {
  display: flex; 
  align-items: center;
  justify-content: space-between;
  background-color: #2c3e50;
  color: #fff;
  padding: 20px;
  z-index: 100000;
}

.main-header h1 {
  margin: 0;
  font-size: 28px;
}

.nav {
  margin: 0;
}

.nav-toggle {
  display: none;
}

/* Hamburger icon (hidden on desktop) */
.nav-toggle-label {
  display: none;
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
  width: 30px;
  height: 30px;
}

.nav-toggle-label span,
.nav-toggle-label span::before,
.nav-toggle-label span::after {
  display: block;
  background: #fff;
  height: 4px;
  width: 100%;
  border-radius: 2px;
  position: absolute;
  transition: all 0.3s ease;
}

.nav-toggle-label span {
  top: 50%;
  transform: translateY(-50%);
}

.nav-toggle-label span::before {
  content: "";
  top: -10px;
}

.nav-toggle-label span::after {
  content: "";
  top: 10px;
}

/* Navigation menu */
.nav-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 20px;
  align-items: center;
}

.nav-menu li a,
.nav-menu li button {
  color: #fff;
  text-decoration: none;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
}

/* -----------------------------------
   Database Selector Trigger Styles
----------------------------------- */
.db-selector-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff; /* Ensures label is white */
  padding: 6px 10px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

/* On hover, slightly darken the background so the user knows it’s interactive. */
.db-selector-trigger:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.db-selector-trigger label {
  font-weight: 500;
}

/* --- The SELECT element --- */
.db-selector-trigger select {
  color: #fff;
  background-color: transparent;
  font-size: 14px;
  cursor: pointer;

  /* Remove default OS styles for the dropdown arrow */
  -webkit-appearance: none;  /* Chrome, Safari, Opera */
  -moz-appearance: none;     /* Firefox */
  appearance: none;

  /* Hide the default arrow on IE10/IE11 */
  /* (In some cases you may need prefix variations) */
  &::-ms-expand {
    display: none;    
  }

  /* Add some padding so your custom icon won't overlap text */
  padding: 4px 28px 4px 4px;
  border: none;          /* remove default border */
  outline: none;         /* remove focus outline, or style it manually if you prefer */
}

/* Optional: Hover/focus effect for the select itself */
.db-selector-trigger select:hover {
  background-color: rgba(255, 255, 255, 0.10);
}

/* When the select is clicked (focused/open) */
.db-selector-trigger select:focus {
  /* A light background helps the black text stand out */
  background-color: #fff; 
  color: #000; /* Change font color to black */
}

/* User menu styles */
.user-menu {
  position: relative;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  transition: background-color 0.2s;
}

.user-menu-trigger:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.dropdown-arrow {
  font-size: 10px;
  margin-left: 6px;
  transition: transform 0.2s ease;
}

.arrow-up {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 220px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 12px;
  margin-top: 8px;
  z-index: 1000;
  color: #333;
}

.user-info {
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  margin-bottom: 10px;
}

.user-email {
  font-size: 14px;
  word-break: break-all;
}

.user-role {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.menu-options {
  margin-top: 8px;
}

.menu-option-btn {
  width: 100%;
  padding: 10px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  text-align: left;
}

.menu-option-btn:hover {
  background-color: #e0e0e0;
}

.logout-btn {
  color: #e53935;
}

/* Responsive styles for mobile devices */
@media (max-width: 768px) {
  .nav-toggle-label {
    display: block;
  }
  .nav-menu {
    position: absolute;
    top: 60px;
    right: 0;
    background: #2c3e50;
    flex-direction: column;
    width: 200px;
    padding: 20px;
    display: none;
    align-items: flex-start;
  }
  .nav-toggle:checked + .nav-toggle-label + .nav-menu {
    display: flex;
  }
  
  .user-dropdown {
    position: static;
    width: 100%;
    margin-top: 10px;
    box-shadow: none;
    padding: 10px;
    display: none;
  }
  
  .user-menu-trigger {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
